import { useTranslation } from "next-i18next";
import { useChangeEffect } from "@smile2impress/attraction-utils";
import { Box } from "@smile2impress/components/relaunch";
import {
    LeadFormProvider,
    LeadForm as LeadFormComponent,
    FormDataType,
    ExperimentIdType,
    TreatmentType,
    PatientCategory,
} from "@smile2impress/lead-form";
import { useRouter } from "next/router";
import { ClinicType, getClinicOptionId } from "@smile2impress/location-content";
import { getCookiesOrigin } from "@smile2impress/attraction-utils";
import { useCallback, useEffect, useState } from "react";
import { UTSLocale } from "@smile2impress/attraction-constants";
import { useUTSLocaleContext } from "context/utsLocaleContext";
import { axiosInstanceV2 } from "api/global";
import { useLocationContext } from "context/locationContext";
import { useRecaptchaToken } from "hooks/useRecaptchaToken";
import {
    SMSVerificationRequiredSetUpType,
    verificationRequiredByQueryParam,
} from "hooks/useIsSmsVerificationRequired";

import { removeQueryParams } from "utils/next/router/removeQueryParams";
import { getAppMode } from "utils/getAppMode";
import {
    getRefCodeFromQuery,
    getReferralSourceFromQuery,
} from "utils/referral";
import { promotionUrlConfig } from "configs/urlsConfig";
import { COOKIES } from "constants/enum";
import { GTMEvents } from "services/AnaliticsService/events";
import { sendGTMEvent } from "services/AnaliticsService/sendGoogleAnalitic";

import { LeadFormProps } from "./types";
import {
    getAdditionalProperties,
    handleError,
    handleSendAnalytics,
} from "./lib";

export const LeadForm = ({
    experimentId,
    withPromoCode = false,
    withTreatmentType = true,
    defaultTreatmentType = TreatmentType.ORTHODONTICS,
    navigatePathname,
    navigateThanksPathname,
    isDrs = false,
    isSmajla = false,
    isSMSRequired = false,
    refCode,
    referralSource,
    instagramBypass = false,
    withQuickClinicSelection = false,
    withGeoSelection,
    withPatientCategory = false,
    defaultPatientCategory = PatientCategory.ADULT,
}: LeadFormProps) => {
    const { i18n } = useTranslation();
    const { locale } = useUTSLocaleContext();
    const router = useRouter();
    const getRecaptchaToken = useRecaptchaToken("lead_form_submit");
    const cookiesOrigin = getCookiesOrigin();
    const [smsRequired, setSMSRequired] = useState(isSMSRequired);

    const { push, query, isReady } = router;

    const { city } = useLocationContext();
    const { isLiveMode } = getAppMode();

    // Fall back to the `?code=` / `?referral_source=` URL params so referral
    // leads are attributed even when the props aren't passed explicitly (e.g.
    // the CMS-driven referral page renders LeadForm via the generic component).
    const resolvedRefCode = refCode ?? getRefCodeFromQuery();
    const resolvedReferralSource =
        referralSource ?? getReferralSourceFromQuery();

    const defaultCity = Array.isArray(query.location)
        ? query.location[0]
        : query.location;
    const defaultClinic = query.clinicId
        ? getClinicOptionId(
              parseInt(
                  Array.isArray(query.clinicId)
                      ? query.clinicId[0]
                      : query.clinicId
              ),
              query.type === ClinicType.Teens ? ClinicType.Teens : undefined
          )
        : undefined;

    useChangeEffect(
        ([prevCity]) => {
            if (prevCity && city && prevCity.id !== city.id) {
                removeQueryParams(router, "location", "clinicId", "type");
            }
        },
        [city]
    );

    useEffect(() => {
        const smsRequiredFromCookies = Boolean(
            verificationRequiredByQueryParam(
                COOKIES.IS_LEAD_SMS_VERIFICATION_REQUIRED_SET_UP
            )
        );
        const smsRequiredFromQuery =
            new URLSearchParams(window.location.search).get("lead-sms") ===
            SMSVerificationRequiredSetUpType.SMS_VERIFICATION_REQUIRED_SET_UP_ON;
        setSMSRequired(smsRequiredFromQuery || smsRequiredFromCookies);
    }, []);

    const withPatientCategoryByCountry =
        withPatientCategory === undefined
            ? [
                  UTSLocale.SPAIN_ES,
                  UTSLocale.PORTUGAL_PT,
                  UTSLocale.ITALY_IT,
              ].includes(locale as UTSLocale)
            : withPatientCategory;

    const handleFormViewed = useCallback(() => {
        sendGTMEvent(GTMEvents.FORM_VIEWED_GTM_EVENT);
    }, []);

    // Exclude Denmark from geolocation
    const geoSelectionEnabled =
        withGeoSelection ?? locale !== UTSLocale.DENMARK_DK;

    return (
        <Box>
            {isReady && (
                <LeadFormProvider key={locale}>
                    <LeadFormComponent
                        key={locale}
                        options={{
                            i18n: { i18n },
                            locale,
                            urlsConfig: {
                                website: "",
                                PA: process.env.PERSONAL_AREA_URL!,
                                promotion: promotionUrlConfig[locale],
                            },
                            withGeoSelection: geoSelectionEnabled,
                            cookieConfig: isLiveMode
                                ? { domain: cookiesOrigin }
                                : undefined,
                            ...(!!experimentId && {
                                experimentConfig: { experimentId },
                            }),
                            defaultCity,
                            defaultClinic,
                            withPromoCode,
                            withTreatmentType,
                            withPatientCategory: withPatientCategoryByCountry,
                            defaultTreatmentType,
                            defaultPatientCategory,
                            isDrs,
                            isSmajla,
                            isSMSRequired: smsRequired,
                            withQuickClinicSelection,
                        }}
                        axiosInstance={axiosInstanceV2}
                        navigate={push}
                        navigatePathname={navigatePathname}
                        navigateThanksPathname={navigateThanksPathname}
                        getAdditionalProperties={(
                            data: FormDataType,
                            experimentGroupId?: ExperimentIdType
                        ) =>
                            getAdditionalProperties(
                                locale,
                                data,
                                experimentGroupId,
                                resolvedReferralSource
                            )
                        }
                        additionalContactData={{ refCode: resolvedRefCode }}
                        getRecaptchaToken={getRecaptchaToken}
                        onSubmitSuccess={(data, externalIds) =>
                            handleSendAnalytics(data, locale, externalIds)
                        }
                        onSubmitError={handleError}
                        onFormViewed={handleFormViewed}
                        instagramBypass={instagramBypass}
                    />
                </LeadFormProvider>
            )}
        </Box>
    );
};
